import { sortArrayBy } from '@domain-group/javascript-utils'

import { pendingStatusKeys } from '@functions/status'

import useCampaignItems from '@hooks/useCampaignItems'
import useOrders from '@hooks/useOrders'
import useSelector from '@hooks/useSelector'

import { EntitiesState } from '@redux/entities'
import type { CampaignModel, OrderModel, StatusModel } from '@models/types'

const hiddenOrderTypes = ['editorial']

type GetGroupKeyParams = {
  order: OrderModel,
  status: StatusModel,
}

const getGroupKey = (params: GetGroupKeyParams) => {
  const { order, status } = params

  // @ts-expect-error TS2345
  const isPending = pendingStatusKeys.includes(status.key)
  const isPublication = order.order_type === 'publication'

  if (isPending && hiddenOrderTypes.includes(order.order_type)){
    return 'hidden'
  }

  if (isPending){
    return isPublication ? 'publication' : 'pending'
  }

  return 'approved'
}

type GroupOrdersByTypeParams = {
  orders: OrderModel[],
  statuses: EntitiesState['statuses'],
}

const groupOrdersByType = (params: GroupOrdersByTypeParams) => {
  const { orders, statuses } = params

  const grouped = {
    approved: [],
    hidden: [],
    pending: [],
    publication: [],
    totalCount: orders.length,
    visible: [],
  }

  orders.forEach((order) => {
    const status = order.status_id && statuses[order.status_id] ? statuses[order.status_id] : {}

    // @ts-expect-error TS2739
    const groupKey = getGroupKey({ order, status })

    // @ts-expect-error TS2345
    grouped[groupKey].push(order)
    // @ts-expect-error TS2345
    if (groupKey !== 'hidden') grouped.visible.push(order)
  })

  return grouped
}

type UseCampaignItemsAndOrdersOptions = {
  campaign: CampaignModel,
  performHttpRequests?: boolean,
  requestOptions?: {
    [key: string]: boolean | string | undefined,
  },
}

function useCampaignItemsAndOrders(options: UseCampaignItemsAndOrdersOptions) {
  const {
    campaign, performHttpRequests = false, requestOptions,
  } = options

  const statuses = useSelector(reduxAction => reduxAction.entities.statuses)
  const { loading: loadingStatuses } = useSelector(reduxAction => reduxAction.statuses)

  // Completed Orders
  const completedOrdersPayload = useOrders({
    filters: {
      campaign_id: campaign.id,
      order_by: 'id desc',
      status_group_key: 'complete',
    },
    performHttpRequests,
    requestOptions,
  })

  const {
    loading: loadingCompletedOrders,
    filteredOrders: filteredCompletedOrders,
  } = completedOrdersPayload

  // Pending Orders
  const pendingOrdersPayload = useOrders({
    filters: {
      campaign_id: campaign.id,
      order_by: 'id desc',
      status_group_key: 'pending',
    },
    performHttpRequests,
    requestOptions,
  })

  const {
    loading: loadingPendingOrders,
    filteredOrders: filteredPendingOrders,
    hasFilteredOrders,
  } = pendingOrdersPayload

  const filteredOrders = [...filteredPendingOrders, ...filteredCompletedOrders]
  const sortedOrders = sortArrayBy(filteredOrders, 'asc', order => order.publication_edition_id || order.id)
  const groupedOrders = groupOrdersByType({ orders: sortedOrders, statuses })

  const allOrdersHaveQuoteItemId = !!groupedOrders.visible.length
    // @ts-expect-error TS2339
    && groupedOrders.visible.every(order => order.campaign_quote_item_id)

  // Campaign Items
  const campaignItemsPayload = useCampaignItems({
    filters: {
      campaign_id: campaign.id,
    },
    performHttpRequests,
  })

  const {
    filteredCampaignItems,
    hasFilteredCampaignItems,
    loading: loadingCampaignItems,
  } = campaignItemsPayload

  const allCampaignItemsHaveQuoteItemId = !!filteredCampaignItems.length
    // @ts-expect-error TS2551
    && filteredCampaignItems.every(campaignItem => campaignItem.campaign_quote_item_id)

  // orders or campaign items
  const canGenerateSchedule = hasFilteredOrders || hasFilteredCampaignItems

  // Order Limit - Dont let user create more than 100 orders
  const orderLimitReached = groupedOrders.totalCount >= 100

  return {
    allCampaignItemsHaveQuoteItemId,
    allOrdersHaveQuoteItemId,
    canGenerateSchedule,
    filteredCampaignItems,
    hasFilteredCampaignItems,
    groupedOrders,
    loading: loadingCampaignItems || loadingCompletedOrders || loadingPendingOrders || loadingStatuses,
    orderLimitReached,
  }
}

export default useCampaignItemsAndOrders
